import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Bed, Bath, MapPin, Car } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatCurrency } from '@/lib/utils';
import type { Property } from '@/types';

interface PropertyCardProps {
  property: Property;
  corretorSlug: string;
  index?: number;
  showStatus?: boolean;
}

export default function PropertyCard({ property, corretorSlug, index = 0, showStatus = false }: PropertyCardProps) {
  const featuredImage = property.featured_image_url || "https://images.pexels.com/photos/1396132/pexels-photo-1396132.jpeg";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      whileHover={{ y: -5 }}
      className="h-full"
    >
      <Link to={`/${corretorSlug}/imoveis/${property.id}`} className="block h-full">
        <Card className="overflow-hidden h-full flex flex-col hover:shadow-lg transition-all duration-300 cursor-pointer">
          <div className="relative aspect-[4/3] overflow-hidden">
            <motion.img 
              src={featuredImage}
              alt={property.title}
              className="w-full h-full object-cover"
              loading="lazy"
              initial={{ scale: 1 }}
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3 }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            
            {/* Category Badge */}
            {property.category && (
              <div className="absolute top-4 left-4">
                <Badge variant="secondary" className="bg-background/80 backdrop-blur-sm border-primary/20">
                  {property.category}
                </Badge>
              </div>
            )}

            {/* Status Badge */}
            {showStatus && property.status !== 'disponivel' && (
              <div className="absolute top-4 right-4">
                <Badge variant="destructive" className="bg-destructive/90 backdrop-blur-sm">
                  {property.status === 'vendido' ? 'Vendido' : 'Alugado'}
                </Badge>
              </div>
            )}
          </div>

          <CardContent className="p-3 flex-1 flex flex-col">
            <div className="flex-1">
              {/* Price */}
              <div className="text-lg font-bold text-primary">
                {formatCurrency(property.price)}
              </div>

              {/* Title */}
              <h3 className="font-semibold text-sm leading-tight line-clamp-2 mt-1">
                {property.title}
              </h3>

              {/* Location */}
              {property.city && (
                <div className="flex items-center text-xs text-muted-foreground mt-1">
                  <MapPin className="h-3 w-3 mr-1 flex-shrink-0" />
                  <span className="truncate">{property.city}, {property.state}</span>
                </div>
              )}

              {/* Property Details - Only bedrooms, bathrooms, and parking */}
              <div className="grid grid-cols-3 gap-1 mt-2">
                <div className="flex items-center justify-center">
                  <Bed className="h-4 w-4 mr-1 text-primary" />
                  <span className="text-xs font-medium">{property.bedrooms}</span>
                </div>
                <div className="flex items-center justify-center">
                  <Bath className="h-4 w-4 mr-1 text-primary" />
                  <span className="text-xs font-medium">{property.bathrooms}</span>
                </div>
                <div className="flex items-center justify-center">
                  <Car className="h-4 w-4 mr-1 text-primary" />
                  <span className="text-xs font-medium">{property.parking_spaces}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </Link>
    </motion.div>
  );
}